import { Spacing } from '@persona-id/design-tokens';
import { SearchIcon } from '@persona-id/design-tokens/icons/16px';
import Fuse from 'fuse.js';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import ChecksTableRow from 'dashboard/components/ChecksTable/ChecksTableRow';
import ChecksTableRowMultipleChecks from 'dashboard/components/ChecksTable/ChecksTableRowMultipleChecks';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableLayout, { TableNullState, TableTH } from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Check, CheckCategory, CheckStatus, MatchRequirement } from 'dashboard/models/check';
import { Verification } from 'dashboard/models/verification';
import Input, { InputGroup } from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import { CHECK_CATEGORY_DISPLAY, CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { ApiModel } from 'lib/interfaces/api';
import { CheckName } from 'lib/verificationCheck';

export type AdditionalPhotos = {
  frontPhotoUrl?: string;
  backPhotoUrl?: string;
};

interface ChecksTableProps {
  checks: Check[];
  title?: string;
  verification?: Verification; // This is only needed when a custom row is used to display consolidated check results
  additionalPhotos?: AdditionalPhotos;
}

const ChecksTable = ({
  checks,
  title = 'Checks',
  verification,
  additionalPhotos,
}: ChecksTableProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const handleSearchTermChange = (value: string) => setSearchTerm(value);

  // Used to ensure that ChecksTableRowMultipleChecks is only rendered once per Verification
  let multipleChecksRowComponentRendered = false;

  interface ChecksFilterParams {
    category: CheckCategory[];
    requirement: MatchRequirement[];
  }
  const [filter, setFilter] = useState<ChecksFilterParams>({ category: [], requirement: [] });
  const [sortParams, setSortParams] = useState<SortParams>({
    sort: '-',
    shouldSortDescending: false,
  });

  const handleTypeSelectChange = (values: CheckCategory[]) => {
    setFilter({
      ...filter,
      category: values,
    });
  };

  const handleRequirementSelectChange = (values: MatchRequirement[]) => {
    setFilter({
      ...filter,
      requirement: values,
    });
  };

  const handleColumnHeaderClick = (columnKey: string) => {
    setSortParams((prevSortParams) => {
      if (prevSortParams.sort === columnKey) {
        return {
          sort: columnKey,
          shouldSortDescending: !prevSortParams.shouldSortDescending,
        };
      }

      return {
        sort: columnKey,
        shouldSortDescending: false,
      };
    });
  };

  const compareChecks = (a: Check, b: Check) => {
    const { sort: sortKey, shouldSortDescending } = sortParams;

    const requirementOrder = {
      [MatchRequirement.Required]: 1,
      [MatchRequirement.RequiresRetry]: 2,
      [MatchRequirement.NotRequired]: 3,
    };

    const statusOrder = {
      [CheckStatus.Failed]: 1,
      [CheckStatus.Passed]: 2,
      [CheckStatus.NotApplicable]: 3,
    };

    let comparisonResult = 0;

    switch (sortKey) {
      case 'status':
        comparisonResult = statusOrder[a.status] - statusOrder[b.status];
        break;
      case 'name':
        comparisonResult = CHECK_DISPLAY_CONFIG[a.name].name.localeCompare(
          CHECK_DISPLAY_CONFIG[b.name].name,
        );
        break;
      case 'type':
        comparisonResult = CHECK_DISPLAY_CONFIG[a.name].category.localeCompare(
          CHECK_DISPLAY_CONFIG[b.name].category,
        );
        break;
      case 'required':
        comparisonResult = requirementOrder[a.requirement] - requirementOrder[b.requirement];
        break;
      default:
        // Default sorting: first by requirement, then by status
        comparisonResult =
          requirementOrder[a.requirement] - requirementOrder[b.requirement] ||
          statusOrder[a.status] - statusOrder[b.status];
        break;
    }

    return shouldSortDescending ? -comparisonResult : comparisonResult;
  };

  // Filters checks by type & requirement, and then sorts according to compareChecks
  const filteredAndSortedChecks = useMemo(() => {
    const filteredChecks = checks.filter((check) => {
      // Filter out any check that doesn't exist in the display config
      if (!(check.name in CHECK_DISPLAY_CONFIG)) {
        return false;
      }

      // 'RequiresRetry' doesn't appear on filter dropdown, but should be involved when searching 'Required'
      const requirement = filter.requirement.includes(MatchRequirement.Required)
        ? [...filter.requirement, MatchRequirement.RequiresRetry]
        : filter.requirement;

      return (
        (requirement.length === 0 || requirement.includes(check.requirement)) &&
        (filter.category.length === 0 ||
          filter.category.includes(CHECK_DISPLAY_CONFIG[check.name].category))
      );
    });

    return [...filteredChecks].sort(compareChecks);
  }, [checks, filter, searchTerm, sortParams]);

  const filteredAndSortedChecksWithDisplayName = filteredAndSortedChecks.map((check) => {
    return {
      ...check,
      displayName: CHECK_DISPLAY_CONFIG[check.name].name,
    };
  });

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [
        { name: 'displayName', weight: 0.8 },
        { name: 'name', weight: 0.5 },
        { name: 'description', weight: 0.2 },
      ],
    };

    return new Fuse(filteredAndSortedChecksWithDisplayName, fuseOptions);
  }, [filteredAndSortedChecksWithDisplayName]);

  const renderedChecks =
    searchTerm === ''
      ? filteredAndSortedChecksWithDisplayName
      : fuse.search(searchTerm).map(({ item }) => item);

  return (
    <ChecksTableWrapper>
      <HeadingWrapper>
        <Heading size='md'>{title}</Heading>
        <HeadingFilterers>
          <HeadingSearchWrapper size='md' leftIcon={<SearchIcon />}>
            <Input
              id='checks-table-v2__search-input'
              name='checks-table-v2__search-input'
              aria-label='checks-table-v2__search-input'
              placeholder='Search...'
              value={searchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
            />
          </HeadingSearchWrapper>
          <MultiSelect
            value={filter.category}
            selectOptionValues={TYPE_FILTER_OPTIONS}
            labelTextMapper={useLabelTextMapper(TYPE_FILTER_OPTIONS, 'types', {
              emptyIsAll: true,
            })}
            onChange={handleTypeSelectChange}
          />
          <MultiSelect
            value={filter.requirement}
            selectOptionValues={REQUIREMENT_FILTER_OPTIONS}
            labelTextMapper={useLabelTextMapper(REQUIREMENT_FILTER_OPTIONS, 'requirements', {
              emptyIsAll: true,
            })}
            onChange={handleRequirementSelectChange}
          />
        </HeadingFilterers>
      </HeadingWrapper>
      <Spacer height={2} />
      <TableLayout
        dataTest='checks-table'
        headerTR={
          <>
            <ChecksTableTH
              canSort={true}
              isSorted={sortParams.sort === 'status'}
              isDescending={sortParams.shouldSortDescending}
              onClick={() => handleColumnHeaderClick('status')}
              hasLeftPadding={true}
            >
              Status
            </ChecksTableTH>
            <ChecksTableTH
              canSort={true}
              isSorted={sortParams.sort === 'name'}
              isDescending={sortParams.shouldSortDescending}
              onClick={() => handleColumnHeaderClick('name')}
            >
              Check name
            </ChecksTableTH>
            <ChecksTableTH
              canSort={true}
              isSorted={sortParams.sort === 'type'}
              isDescending={sortParams.shouldSortDescending}
              onClick={() => handleColumnHeaderClick('type')}
            >
              Type
            </ChecksTableTH>
            <ChecksTableTH
              canSort={true}
              isSorted={sortParams.sort === 'required'}
              isDescending={sortParams.shouldSortDescending}
              onClick={() => handleColumnHeaderClick('required')}
            >
              Required
            </ChecksTableTH>
          </>
        }
        isEmpty={renderedChecks.length === 0}
        nullState={
          <TableNullState>
            <b>No Verification Checks found</b>
          </TableNullState>
        }
      >
        {renderedChecks.map((check, index) => {
          if (verification && ATTRIBUTE_CHECKS_USED_IN_CUSTOM_ROWS.includes(check.name)) {
            if (multipleChecksRowComponentRendered) return null;
            multipleChecksRowComponentRendered = true;

            return (
              <ChecksTableRowMultipleChecks
                key={`checks-table-v2__row-${index}`}
                verification={verification}
                name={getMultipleChecksRowName(verification)}
              />
            );
          }

          return (
            <ChecksTableRow
              key={`checks-table-v2__row-${index}`}
              check={check}
              additionalPhotos={additionalPhotos}
            />
          );
        })}
      </TableLayout>
    </ChecksTableWrapper>
  );
};

// These checks are displayed as part of a ChecksTableRowMultipleChecks component,
// so we do not display them as standalone rows. However, we do allow users to search
// for the check name and have the custom component render if the check name matches.
// Note: Verification types that use a custom row for many of its checks may still
// contain checks that are represented in standalone rows (e.g. AamvaServiceAvailableDetection).
const ATTRIBUTE_CHECKS_USED_IN_CUSTOM_ROWS: CheckName[] = [
  CheckName.AamvaAddressComparison,
  CheckName.AamvaBirthdateComparison,
  CheckName.AamvaExpirationDateComparison,
  CheckName.AamvaIdentificationNumberComparison,
  CheckName.AamvaIdentityComparison,
  CheckName.AamvaIssueDateComparison,
  CheckName.AamvaNameComparison,
  CheckName.BusinessWebsiteIdentityComparison,
  CheckName.BusinessWebsiteWebsiteComparison,
  CheckName.DatabaseAadhaarBirthdateComparison,
  CheckName.DatabaseAadhaarNameComparison,
  CheckName.DatabaseAddressComparison,
  CheckName.DatabaseBirthdateComparison,
  CheckName.DatabaseNameComparison,
  CheckName.DatabaseIdentityComparison,
  CheckName.DatabasePhoneNumberComparison,
  CheckName.DatabaseSocialSecurityNumberComparison,
  CheckName.DatabasePhoneCarrierAddressComparison,
  CheckName.DatabasePhoneCarrierBirthdateComparison,
  CheckName.DatabasePhoneCarrierNameFirstComparison,
  CheckName.DatabasePhoneCarrierNameLastComparison,
  CheckName.DatabaseStandardIdentityComparison,
  CheckName.DatabaseVatNumberIdentityComparison,
  CheckName.DatabaseBusinessAiIdentityComparison,
  CheckName.DatabaseBusinessIdentityComparison,
  CheckName.DatabaseBusinessFootprintIdentityComparison,
];

const TYPE_FILTER_OPTIONS = Object.values(CheckCategory).map((category: CheckCategory) => {
  return {
    label: CHECK_CATEGORY_DISPLAY[category],
    value: category,
  };
});

const REQUIREMENT_FILTER_OPTIONS = [
  {
    label: 'Required',
    value: MatchRequirement.Required,
  },
  {
    label: 'Non-required',
    value: MatchRequirement.NotRequired,
  },
];

const getMultipleChecksRowName = (verification: Verification) => {
  const DEFAULT_MULTIPLE_CHECKS_ROW_NAME = 'Attribute Checks';
  const IDENTITY_COMPARISON_NAME = 'Identity Comparison Check';
  const checks = verification.attributes?.checks;

  if (!checks) {
    return DEFAULT_MULTIPLE_CHECKS_ROW_NAME;
  }

  if (verification.type === ApiModel.VerificationAamva) {
    const identityComparisonCheck = checks.find(
      (check) => check.name === CheckName.AamvaIdentityComparison,
    );

    if (
      identityComparisonCheck &&
      identityComparisonCheck.requirement === MatchRequirement.Required
    ) {
      return IDENTITY_COMPARISON_NAME;
    }
  } else if (verification.type === ApiModel.VerificationBusinessWebsite) {
    const identityComparisonCheck = checks.find(
      (check) => check.name === CheckName.BusinessWebsiteIdentityComparison,
    );

    if (identityComparisonCheck && identityComparisonCheck.status !== CheckStatus.NotApplicable) {
      return IDENTITY_COMPARISON_NAME;
    } else {
      return 'Website Comparison';
    }
  } else if (verification.type === ApiModel.VerificationDatabase) {
    const identityComparisonCheck = checks.find(
      (check) => check.name === CheckName.DatabaseIdentityComparison,
    );

    if (identityComparisonCheck) {
      return IDENTITY_COMPARISON_NAME;
    } else {
      return 'Comparison Checks';
    }
  } else if (verification.type === ApiModel.VerificationDatabaseAadhaar) {
    return 'Comparison Checks';
  } else if (verification.type === ApiModel.VerificationDatabaseBusiness) {
    const identityComparisonCheck = checks.find(
      (check) =>
        check.name === CheckName.DatabaseBusinessAiIdentityComparison ||
        check.name === CheckName.DatabaseBusinessIdentityComparison,
    );

    if (identityComparisonCheck) {
      return IDENTITY_COMPARISON_NAME;
    }
  } else if (verification.type === ApiModel.VerificationDatabaseBusinessFootprint) {
    const identityComparisonCheck = checks.find(
      (check) => check.name === CheckName.DatabaseBusinessFootprintIdentityComparison,
    );

    if (identityComparisonCheck) {
      return IDENTITY_COMPARISON_NAME;
    }
  } else if (verification.type === ApiModel.VerificationDatabaseStandard) {
    return IDENTITY_COMPARISON_NAME;
  } else if (verification.type === ApiModel.VerificationDatabasePhoneCarrier) {
    return 'Carrier Match Results';
  } else if (verification.type === ApiModel.VerificationDatabaseVatNumber) {
    const identityComparisonCheck = checks.find(
      (check) => check.name === CheckName.DatabaseVatNumberIdentityComparison,
    );

    if (identityComparisonCheck) {
      return IDENTITY_COMPARISON_NAME;
    }
  }

  return DEFAULT_MULTIPLE_CHECKS_ROW_NAME;
};

const ChecksTableWrapper = styled.div`
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  column-gap: ${Spacing[2]};
`;

const HeadingSearchWrapper = styled(InputGroup)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: -${Spacing[1]};
`;

const HeadingFilterers = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
`;

const ChecksTableTH = styled(TableTH)<{ hasLeftPadding?: boolean }>`
  // For alignment due to the ChevronDownIcon or ChevronRightIcon in the Status column
  ${({ hasLeftPadding }) => hasLeftPadding && `padding-left: 36px;`}
`;

export default ChecksTable;
